import React, { useEffect, useState } from 'react';
import { useShopifyProduct } from '../../../../api/shopify/ShopifyProduct';
import { CTAProps } from './CTAButton';
import { getProductServiceVariantId, getSelectedServiceSuggestion } from '../utils/product-service';
import { hasReachedMaxQty } from '../../../../utils/checkMaxQty/hasReachedMaxQty';
import { showMaxQtyAlert } from '../../../../utils/checkMaxQty/showMaxQtyAlert';
import { trackAddToCart } from '@jbhi-fi/utils/src/ga4Tracking';
import { setMiniCartVisible } from '../utils/utils';
import { useMinicartUiContext } from '../../../../utils/cart/context/MinicartUiContext';
import { getStoreCurrency } from '../../../../utils/shopify/getStoreCurrency';
import { transformShopifyProductForTracking } from '../../../../utils/cart/transformShopifyProductForTracking';
import { getInsight, postAlgoliaAddToCart } from '../../../../utils/algolia/search-insights';
import { AttachAddToCartSlideout } from '../../attachAddToCartSlideout/AttachAddToCartSlideout';
import { useProducts } from '@jbhi-fi/components/src/hooks/useProducts/useProducts';
import { ActionButton } from '@jbhi-fi/components/src/ActionButton';
import { CartActionState } from '@jbhi-fi/components/src/CartProvider/types';
import { ShopifyProductTransformer } from '@jbhi-fi/components/src/Shopify/transformers/ShopifyProductTransformer';
import { getMarketplaceTrackingValuesFromShopify } from '../../../../utils/event-tracking/globalTrackingHelpers';
import { drawCart, getCartLoading, setCartLoading } from '../../../../utils/legacyCart';
import { filterAndMapAttributes } from '@jbhi-fi/apis/src/utils/filterAndMapAttributes';
import { addServiceOffering } from '../../../../utils/cart/addServiceOffering';
import { getServiceOfferings, setServiceOfferings } from '../../../../utils/parseServiceOfferings';
import { ServiceIds } from '../../../../utils/constants/services';
import { getMarketplacePropertiesBySeller } from '../../../../utils/cart/refreshTieredProductDetails';
import { ShopifyStorefrontClient } from '../../../../api/shopify/ShopifyStorefrontClient';
import { transformStorefrontCartToJbCart } from '../../../../utils/cart/transformStorefrontCartToJbCart';
import { ServiceOffering } from '@jbhi-fi/apis/src/services/types';
import { klaviyoAddedToCart } from '../../../../utils/klaviyo';
import { getCartApiResponse } from '../../../../utils/cartApi';

export const ADD_TO_CART_CONFIRMATION_DURATION_MS = 2000;

const storefrontClient = new ShopifyStorefrontClient();

declare let window: any;
export const AddToCart = ({
  productCartData,
  product,
  gtmProduct,
  trackingListName,
  quantity = 1,
}: CTAProps): JSX.Element => {
  const [isLoading, setIsLoading] = useState(false);
  const [buttonState, setButtonState] = useState<CartActionState>(CartActionState.Default);
  const [modalVisible, setIsModalVisible] = useState<boolean>(false);
  const { shopify, productMetafields, marketplaceSellers } = useShopifyProduct();
  const productSku = shopify?.variants[0]?.sku;
  const productVariantId = product.variants[0].id;
  const { products: [algoliaProduct] = [] } = useProducts({ productSkus: [productSku] });
  const { isOpen: isMinicartOpen } = useMinicartUiContext();
  const reviewCount = productMetafields?.Display?.NumberOfProductReviews;
  const reviewScore = productMetafields?.Display?.AverageProductReviewRating;
  const isMarketplaceProduct = ShopifyProductTransformer.isMarketplaceProduct({ product: shopify });
  const isAttachSlideoutEnabled = window.Jbhifi?.attachConfig?.enabledPages?.pdp && !isMarketplaceProduct;
  const shouldFetchServiceSuggestions =
    (productMetafields?.Product?.EligibleForServices ||
      productMetafields?.Product?.EligibleForServices === undefined) &&
    !isMarketplaceProduct;

  useEffect(() => {
    if (buttonState !== CartActionState.Complete) return;
    const timeout = setTimeout(() => setButtonState(CartActionState.Default), ADD_TO_CART_CONFIRMATION_DURATION_MS);
    return () => clearTimeout(timeout);
  }, [buttonState]);

  const onModalClose = () => {
    setIsModalVisible(false);
    window.zE && window.zE('webWidget', 'show');
  };

  const addToCartTrackingData = {
    location: 'pdp',
    currency: getStoreCurrency(),
    addedItems: {
      ...transformShopifyProductForTracking({
        item: productCartData,
        listName: trackingListName ?? 'pdp',
        reviewCount,
        reviewScore,
      }),
      ...getMarketplaceTrackingValuesFromShopify({
        product: shopify,
        marketplaceSellers,
        onlineProduct: productMetafields,
      }),
    },
  };

  const addToCart = async () => {
    if (getCartLoading()) {
      return;
    }

    if (hasReachedMaxQty(productVariantId, quantity || 1)) {
      showMaxQtyAlert();
      return;
    }

    setIsLoading(true);
    setCartLoading(true);
    if (isAttachSlideoutEnabled && algoliaProduct) {
      setMiniCartVisible(false);
    } else {
      setMiniCartVisible(isMinicartOpen);
    }

    window.zE && window.zE('webWidget', 'hide');

    const properties = {
      _availabilityCanPreOrder: productMetafields?.Availability?.CanPreOrder,
      _availabilityFulfilment: productMetafields?.Availability?.Fulfilment,
      _maxQty: productMetafields?.Availability?.Fulfilment === 'Digital' ? 1 : productMetafields.Product?.LimitPerOrder,
      _priceAtc: shopify?.variants[0]?.price,
    };

    const marketplaceSeller = ShopifyProductTransformer.toMarketplaceSeller({
      product: shopify,
      marketplaceSellers,
      onlineProduct: productMetafields,
    });

    const marketplaceProperties = getMarketplacePropertiesBySeller(marketplaceSeller);
    const attributes = filterAndMapAttributes({ ...properties, ...marketplaceProperties });
    const updatedCart = await storefrontClient.addToCart(productVariantId, quantity, attributes);
    let transformedCart = transformStorefrontCartToJbCart(updatedCart);

    // Update Service Offerings
    if (transformedCart) {
      const cartApiResponse = await getCartApiResponse(transformedCart);
      if (cartApiResponse) setServiceOfferings(cartApiResponse);
    }

    // Add selected service offering to cart
    const suggestion = getSelectedServiceSuggestion();
    if (suggestion?.services?.[0].offerings?.length) {
      const serviceType = suggestion.services[0].id;
      const offering = suggestion.services[0].offerings[0];
      const serviceOfferings = getServiceOfferings();
      const addedItem = transformedCart?.items.find(item => item.id === productVariantId.toString());
      if (!addedItem) return;
      const serviceOffering = (serviceOfferings?.[addedItem.line_id] as Array<ServiceOffering>)?.find(
        service => service.typeId === serviceType && service.id.includes(offering.id)
      );
      if (!serviceOffering || !serviceOffering.shouldRender) return;
      const { variantId, sku, typeId, priceInc } = serviceOffering;

      let shopifyVariantId = variantId;
      if (typeId === ServiceIds.ExtraCare) {
        shopifyVariantId = await getProductServiceVariantId(sku, 'extra-care', priceInc);
      }
      if (!shopifyVariantId) return;

      transformedCart = await addServiceOffering({
        variantId: shopifyVariantId,
        location: 'pdp',
        googleTagManager: gtmProduct,
        serviceData: serviceOffering,
        primaryLineItem: addedItem,
        shouldDrawCart: false,
      });
    }

    // Show Slideout
    setIsModalVisible(true);
    setButtonState(CartActionState.Complete);
    setIsLoading(false);

    await drawCart(transformedCart);

    trackAddToCart(addToCartTrackingData);
    postAlgoliaAddToCart(productSku, getInsight(productSku));
    klaviyoAddedToCart(transformedCart);
  };

  return (
    <>
      <ActionButton
        label="Add to cart"
        onClick={addToCart}
        isLoading={isLoading}
        state={buttonState}
        actionFinishedLabel="Added!"
        dataTestId="add-to-cart-button"
        type="action"
      />

      {isAttachSlideoutEnabled && algoliaProduct ? (
        <AttachAddToCartSlideout
          purchasedProduct={algoliaProduct}
          onClose={onModalClose}
          isOpen={modalVisible}
          pageType="pdp"
          shouldFetchServiceSuggestions={shouldFetchServiceSuggestions}
          freeBonusProducts={[]}
        />
      ) : null}
    </>
  );
};

export default AddToCart;
